import React, { useCallback, useState, useMemo } from 'react'
import { Groupbox, ModalFooter, ModalWindow } from 'view/components/panels'
import styled from '@emotion/styled'
import { DialogField, DialogRow } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { Locale, Translation } from 'locale'
import { createStructuredSelector } from 'reselect'
import { connect } from 'react-redux'
import { SelectboxField } from 'view/components/options'
import { ClearingHouseDetail } from 'api'
import { enabledClearingHouseDetailsSelector } from 'selectors/clearingSelectors'
import { ClearingHouseSelectboxField } from 'view/shared/entity'
import { HorizontalLayout, VerticalLayout } from 'view/styles/constants'

export interface DealsDownloadDialogProps {
  clearingHouses: ClearingHouseDetail[]
  onExportCsv: () => unknown
  onExportContingencyFile: (clearingHouseId: string) => unknown
  onClose?: () => unknown
}

const DealDownloadTypes = {
  csv: Locale.translate(Translation.TC_CSV_DEAL_DOWNLOAD),
  contingencyFile: Locale.translate(Translation.TC_CONTINGENCY_DEAL_DOWNLOAD)
}

const mapStateToProps = createStructuredSelector({
  clearingHouses: enabledClearingHouseDetailsSelector
})

const DialogContent = styled(Groupbox)`
  padding: 0 20px;
`

const DealsDownloadDialogComponent: React.FunctionComponent<DealsDownloadDialogProps> = props => {
  const { clearingHouses, onClose, onExportCsv, onExportContingencyFile } = props

  const [downloadType, setDownloadType] = useState(DealDownloadTypes.csv)
  const [clearingHouseId, setClearingHouseId] = useState('')
  const clearingHouseIds = useMemo(() => clearingHouses.map(clearingHouseDetail => clearingHouseDetail.clearingHouse.id), [clearingHouses])

  const onUpdateDownloadType = useCallback(
    (selectedDownloadType: string) => {
      setDownloadType(selectedDownloadType)
      if (selectedDownloadType === DealDownloadTypes.csv) {
        setClearingHouseId('')
      } else if (selectedDownloadType === DealDownloadTypes.contingencyFile && clearingHouseId === '') {
        setClearingHouseId(clearingHouseIds.length > 0 ? clearingHouseIds[0] : '')
      }
    },
    [setDownloadType, clearingHouseId, clearingHouseIds, setClearingHouseId]
  )

  const onUpdateClearingHouseId = useCallback(
    (selectedClearingHouseId: string) => {
      setClearingHouseId(selectedClearingHouseId)
    },
    [setClearingHouseId]
  )

  const onDownload = useCallback(() => {
    if (downloadType === DealDownloadTypes.csv) {
      onExportCsv()
    } else if (downloadType === DealDownloadTypes.contingencyFile) {
      onExportContingencyFile(clearingHouseId)
    }
    if (onClose) {
      onClose()
    }
  }, [downloadType, clearingHouseId, onExportCsv, onExportContingencyFile, onClose])

  const downloadDisabled = downloadType === DealDownloadTypes.contingencyFile && clearingHouseId === ''

  return (
    <ModalWindow title={Locale.translate(Translation.TC_DEAL_DOWNLOAD_TITLE)}>
      <DialogContent>
        <DialogRow verticalLayout={VerticalLayout.Bottom}>
          <DialogField>
            <SelectboxField
              label={Locale.translate(Translation.SC_DEAL_DOWNLOAD_DOWNLOAD_TYPE)}
              onUpdate={onUpdateDownloadType}
              value={downloadType}
              optionKeys={Object.values(DealDownloadTypes)}
            />
          </DialogField>
          <DialogField>
            <ClearingHouseSelectboxField
              label={Locale.translate(Translation.SC_DEAL_DOWNLOAD_CCP)}
              value={clearingHouseId}
              optionKeys={clearingHouseIds}
              onUpdate={onUpdateClearingHouseId}
              disabled={downloadType !== DealDownloadTypes.contingencyFile}
            />
          </DialogField>
        </DialogRow>
      </DialogContent>
      <ModalFooter>
        <DialogRow horizontalLayout={HorizontalLayout.Right}>
          <DialogField flex={0}>
            <TextButton type={TextButtonType.Tertiary} onClick={onClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton disabled={downloadDisabled} type={TextButtonType.Primary} onClick={onDownload}>
              {Locale.translate(Translation.TC_BUTTON_DOWNLOAD)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

export const DealsDownloadDialog = connect(mapStateToProps)(DealsDownloadDialogComponent)
